import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, MessageCircle, Brain } from 'lucide-react';

const QChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Your services data
  const servicesData = {
    growth: {
      title: "THE GROWTH REACTOR",
      modules: [
        "Search Dominance & GEO",
        "Performance Warfare (PPC)",
        "Social Signal Amplification",
        "Brand Narrative & Psychology",
        "eCommerce Acceleration"
      ]
    },
    build: {
      title: "THE DIGITAL CORTEX",
      modules: [
        "Mobile Ecosystems",
        "Enterprise Architecture",
        "SaaS Product Engineering",
        "Legacy Modernization",
        "Dedicated Elite Squads"
      ]
    },
    intelligence: {
      title: "SYNTHETIC INTELLIGENCE",
      modules: [
        "Generative AI Solutions",
        "Machine Learning (ML) Core",
        "Intelligent Process Automation",
        "Computer Vision",
        "Business Intelligence"
      ]
    },
    infrastructure: {
      title: "THE BACKBONE",
      modules: [
        "Cloud Sovereignty",
        "Blockchain & Web3",
        "IoT Ecosystems",
        "DevOps & CI/CD",
        "Cyber-Defense"
      ]
    }
  };

  // Your team members categories
  const teamCategories = [
    "leadership",
    "strategy", 
    "architecture",
    "engineering",
    "growth",
    "outreach",
    "design",
    "finance",
    "legal"
  ];

  // Your careers data
  const careersData = [
    "Senior AI Engineer",
    "Full-Stack Architect", 
    "Growth Hacker",
    "DevOps Engineer",
    "UI/UX Designer"
  ];

  const getResponse = (userInput) => {
    const input = userInput.toLowerCase();
    
    if (input.includes('service') || input.includes('what do') || input.includes('offer')) {
      let response = "🚀 **QUAVRION SERVICES**\n\n";
      Object.keys(servicesData).forEach(key => {
        response += `**${servicesData[key].title}**\n`;
        servicesData[key].modules.slice(0, 2).forEach(module => {
          response += `• ${module}\n`;
        });
        response += "\n";
      });
      response += "Which service interests you most?";
      return response;
    }
    
    else if (input.includes('team') || input.includes('leadership') || input.includes('founder')) {
      let response = "👥 **QUAVRION TEAM**\n\nOur team spans expertise across:\n";
      teamCategories.forEach(category => {
        response += `• ${category.charAt(0).toUpperCase() + category.slice(1)}\n`;
      });
      response += "\nLed by visionary founders with 15+ years experience in tech and strategy.";
      return response;
    }
    
    else if (input.includes('career') || input.includes('job') || input.includes('hire')) {
      let response = "💼 **CAREER OPPORTUNITIES**\n\nCurrently hiring:\n";
      careersData.forEach(job => {
        response += `• ${job}\n`;
      });
      response += "\nSend resume to: careers@quavrion.com\nWe respond within 48 hours.";
      return response;
    }
    
    else if (input.includes('contact') || input.includes('email') || input.includes('phone')) {
      return `📞 **CONTACT QUAVRION**\n\n📍 HQ: Hyderabad, India\n📧 Email: hello@quavrion.com\n📞 Phone: +91 98765 43210\n🌐 Website: quavrion.com\n\nAverage response: 24 hours`;
    }
    
    else if (input.includes('hello') || input.includes('hi') || input.includes('hey')) {
      return "Hello! 👋 I'm Q, your AI assistant. I can tell you about QUAVRION's services, team, career opportunities, or contact information. What would you like to know?";
    }
    
    else {
      return "Thanks for your message! I can help you with:\n• Services & Solutions\n• Team & Leadership\n• Career Opportunities\n• Contact Information\n\nWhat would you like to know more about?";
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { text: input, sender: 'user', timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setIsLoading(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const response = getResponse(userInput);
      const botMessage = {
        text: response,
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        text: "Sorry, I'm having trouble connecting. Please try again later or contact us directly at hello@quavrion.com",
        sender: 'bot',
        timestamp: new Date(),
        type: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickResponse = (text) => {
    setInput(text);
    setTimeout(() => handleSend(), 100);
  };

  const quickResponses = ['Services?', 'Team info', 'Careers', 'Contact', 'Tell me more'];

  return (
    <>
      {/* Chatbot Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 z-50 group"
        aria-label="Open Q AI Assistant"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        
        {/* Tooltip */}
        <div className="absolute -top-12 right-0 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          Chat with Q AI
          <div className="absolute bottom-[-4px] right-4 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      </button>

      {/* Chatbot Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 w-96 h-[500px] bg-gray-900/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden z-50"
          >
            {/* Header - PERFECTED */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                  <Brain className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm tracking-wide">Q AI ASSISTANT</h3>
                  <p className="text-blue-100 text-xs">Online • Secure • Instant</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/30 transition-all duration-200"
                aria-label="Close chatbot"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages - PERFECTED */}
            <div className="h-[calc(100%-140px)] overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">Q</span>
                  </div>
                  <h4 className="text-white font-semibold text-lg mb-2">Hello! I'm Q</h4>
                  <p className="text-gray-300 text-sm mb-4">Your AI assistant for QUAVRION</p>
                  <p className="text-gray-400 text-xs">Ask me about services, team, careers, or contact information</p>
                </div>
              )}
              
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] rounded-2xl p-3 shadow-sm ${
                    msg.sender === 'user' 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-br-none' 
                      : 'bg-gray-800/80 text-gray-100 rounded-bl-none border border-gray-700/50'
                  }`}>
                    <p className="text-sm whitespace-pre-line leading-relaxed">{msg.text}</p>
                    <div className={`text-xs mt-2 flex items-center ${msg.sender === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                      <span>{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-800/80 text-gray-100 rounded-2xl rounded-bl-none p-3 border border-gray-700/50">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-150"></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse delay-300"></div>
                      </div>
                      <span className="text-xs text-gray-400">Q is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input - FIXED LAYOUT */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gray-900/80 backdrop-blur-sm border-t border-gray-700/50">
              {/* Quick Responses - MOVED ABOVE INPUT */}
              <div className="mb-3">
                <div className="flex flex-wrap gap-2">
                  {quickResponses.map((text) => (
                    <button
                      key={text}
                      onClick={() => handleQuickResponse(text)}
                      className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-700/50 hover:border-blue-500/30"
                      disabled={isLoading}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>

              {/* Input and Send Button */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Type your message..."
                  className="flex-1 bg-gray-800/70 border border-gray-700/70 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 text-sm transition-all"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSend}
                  disabled={isLoading || !input.trim()}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl px-4 py-2.5 font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-1 text-sm min-w-[80px] justify-center"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Send</span>
                      <Send className="w-3 h-3" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QChatbot;